import React from 'react';

export const STATUSES = ['active', 'pending', 'needs_followup', 'proposed', 'approved', 'rejected', 'dismissed'];
export const TIER_FILTERS = [
  ['all', 'All tiers'],
  ['blocking', 'Blocking / needs fix'],
  ['correction', 'Corrections'],
  ['suggestion', 'Suggestions'],
];
export const TYPE_FILTERS = [
  ['all', 'All types'],
  ['warning', 'Warnings'],
  ['field_update', 'Field updates'],
  ['card_attribute', 'Card attributes'],
  ['card_create', 'Create cards'],
  ['signal', 'Signals'],
  ['new_listing', 'New listings'],
];
export const SOURCE_FILTERS = [
  ['all', 'All sources'],
  ['enrichment', 'Enrichment'],
  ['system', 'System'],
  ['scrape_agent', 'Scrape'],
  ['business_intake', 'Business intake'],
  ['guest_place_suggestion', 'Guest suggestions'],
  ['guest_report', 'Guest reports'],
];
export const PROPOSAL_TIER_ORDER = { blocking: 0, correction: 1, suggestion: 2 };
export const PROPOSAL_TIER_SECTIONS = [
  ['blocking', 'Blocking / needs fix', 'These items block publishing or require a card fix.'],
  ['correction', 'Corrections', 'Field-level changes that may update place or card content.'],
  ['suggestion', 'Suggestions', 'Optional context. Suggestions never block publishing.'],
];

export function labelize(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase()) || '-';
}

export function valueText(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function proposalPayload(item) {
  return item?.payload || item || {};
}

export function proposalKind(item) {
  return item?.item_type || proposalPayload(item).item_type || proposalPayload(item).request_kind || 'proposal';
}

export function proposalTier(item) {
  if (item?.blocking || item?.tier === 'blocking') return 'blocking';
  if (item?.tier === 'correction' || proposalKind(item) === 'field_update') return 'correction';
  return 'suggestion';
}

export function proposalTierLabel(item) {
  if (proposalKind(item) === 'warning' && proposalPayload(item).clearing === 'computed') return 'Needs fix';
  return labelize(proposalTier(item));
}

export function groupTierLabel(group) {
  if (
    group?.tier === 'blocking'
    && group?.blocking_count > 0
    && group?.computed_blocking_count === group?.blocking_count
  ) {
    return 'Needs fix';
  }
  return labelize(group?.tier || 'suggestion');
}

export function groupAttentionText(group) {
  if (!group?.blocking_count) return '';
  if (group.computed_blocking_count === group.blocking_count) {
    return `${group.blocking_count} needs fix`;
  }
  return `${group.blocking_count} blocking`;
}

export function normalizeSourceType(value) {
  const normalized = String(value || 'system').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!normalized || normalized === 'systems') return 'system';
  return normalized;
}

export function proposalSourceType(item) {
  return normalizeSourceType(item?.source?.type || proposalPayload(item).source_type || 'system');
}

export function proposalStatus(item) {
  const payload = proposalPayload(item);
  return item?.status || payload.status || payload.overall_status || payload.field_status || 'pending';
}

export function proposalFieldRequestId(item) {
  const id = item?.id || '';
  const match = String(id).match(/^field:(\d+):/);
  return match ? Number(match[1]) : Number(proposalPayload(item).change_request_id || 0);
}

export function proposalFieldId(item) {
  const id = item?.id || '';
  const match = String(id).match(/^field:\d+:(\d+)$/);
  return match ? Number(match[1]) : proposalPayload(item).id;
}

export function proposalTitle(item) {
  const payload = proposalPayload(item);
  return item.target_label || payload.title || payload.display_text || payload.warning_code || `Proposal ${item.source_item_id || ''}`;
}

export function groupSourceText(group) {
  const sources = group?.sources || [];
  return sources.length ? sources.map(labelize).join(', ') : '-';
}

export function facetCount(facets, key, value) {
  const rows = facets?.[key] || [];
  const row = rows.find(item => String(item.value) === String(value));
  return row?.count || 0;
}

export function optionRowsWithFacetValues(baseRows, facets, key) {
  const seen = new Set(baseRows.map(([value]) => String(value)));
  const dynamicRows = (facets?.[key] || [])
    .filter(row => !seen.has(String(row.value)))
    .map(row => [row.value, row.label || labelize(row.value), row.count]);
  return [...baseRows, ...dynamicRows];
}

export function facetChipText(label, count) {
  return count ? `${label} · ${count}` : label;
}

export function proposalKindLabel(kind) {
  const labels = {
    field_update: 'Field update',
    card_attribute: 'Card attribute',
    card_create: 'Create card',
    new_listing: 'New listing',
    signal: 'Signal',
    match_signal_assignment: 'Match signals',
    warning: 'Readiness warning',
  };
  return labels[kind] || labelize(kind);
}

export function proposalHelpText(kind, tier) {
  if (kind === 'field_update') return 'Review one proposed field change. Approve applies it to the existing place or card.';
  if (kind === 'card_attribute') return 'Review one governed card attribute. Approved attributes reach Composer only after publish projection.';
  if (kind === 'card_create') return 'Review a proposed new card for an existing place. Accepting creates a draft card only; it does not approve or publish.';
  if (kind === 'new_listing') return 'This is not attached to an existing place yet. Create a new listing or merge it into an existing place.';
  if (kind === 'signal') return tier === 'blocking'
    ? 'This worker or system signal blocks publishing until reviewed.'
    : 'This signal is optional context. It can become an agent note, a tag, or be kept/dismissed.';
  if (kind === 'warning') return 'This readiness warning explains why a place or card is not publishable. Resolve only after the issue is fixed or verified.';
  return 'Review this proposal item and choose the appropriate verdict.';
}

export function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-2 border-b border-gray-50 py-1 last:border-0">
      <span className="w-36 flex-shrink-0 text-xs text-gray-500">{label}</span>
      <span className="break-words text-xs text-gray-800">{valueText(value)}</span>
    </div>
  );
}

const PILL_TONES = {
  neutral: {
    idle: 'border-stone-200 bg-white text-stone-600 hover:bg-stone-50',
    active: 'border-stone-900 bg-stone-900 text-white',
  },
  stone: {
    idle: 'border-stone-200 bg-stone-50 text-stone-700 hover:bg-stone-100',
    active: 'border-stone-700 bg-stone-700 text-white',
  },
  red: {
    idle: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
    active: 'border-red-600 bg-red-600 text-white',
  },
  blue: {
    idle: 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100',
    active: 'border-blue-600 bg-blue-600 text-white',
  },
  green: {
    idle: 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100',
    active: 'border-green-600 bg-green-600 text-white',
  },
  purple: {
    idle: 'border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100',
    active: 'border-purple-600 bg-purple-600 text-white',
  },
  amber: {
    idle: 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100',
    active: 'border-amber-600 bg-amber-600 text-white',
  },
  orange: {
    idle: 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100',
    active: 'border-orange-600 bg-orange-600 text-white',
  },
  teal: {
    idle: 'border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100',
    active: 'border-teal-600 bg-teal-600 text-white',
  },
  cyan: {
    idle: 'border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100',
    active: 'border-cyan-600 bg-cyan-600 text-white',
  },
  sky: {
    idle: 'border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100',
    active: 'border-sky-600 bg-sky-600 text-white',
  },
  emerald: {
    idle: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    active: 'border-emerald-600 bg-emerald-600 text-white',
  },
  rose: {
    idle: 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100',
    active: 'border-rose-600 bg-rose-600 text-white',
  },
  indigo: {
    idle: 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100',
    active: 'border-indigo-600 bg-indigo-600 text-white',
  },
  violet: {
    idle: 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100',
    active: 'border-violet-600 bg-violet-600 text-white',
  },
};

export function pillToneClass(tone = 'neutral', active = false) {
  const classes = PILL_TONES[tone] || PILL_TONES.neutral;
  return active ? classes.active : classes.idle;
}

export function pillClass(tone = 'neutral', active = false, size = 'sm') {
  const padding = size === 'filter' ? 'px-3 py-1.5' : size === 'label' ? 'px-2.5 py-1' : 'px-2 py-0.5';
  return `inline-flex items-center rounded-full border ${padding} text-xs font-medium transition-colors ${pillToneClass(tone, active)}`;
}

export function tierTone(kind = 'suggestion') {
  if (kind === 'blocking') return 'red';
  if (kind === 'correction') return 'blue';
  return 'green';
}

const FALLBACK_PROPOSAL_TONES = ['purple', 'teal', 'orange', 'green', 'cyan', 'emerald', 'rose', 'indigo', 'violet', 'sky'];

function stableToneForText(value) {
  const text = String(value || '').trim();
  if (!text) return 'stone';
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash * 31) + text.charCodeAt(index)) % 2147483647;
  }
  return FALLBACK_PROPOSAL_TONES[hash % FALLBACK_PROPOSAL_TONES.length];
}

export function proposalLabelTone(label) {
  const value = String(label || '').toLowerCase();
  if (value.includes('retrieval lane')) return 'indigo';
  if (value.includes('category')) return 'purple';
  if (value.includes('budget')) return 'emerald';
  if (value.includes('season')) return 'amber';
  if (value.includes('card tag') || value.includes('tag')) return 'green';
  if (value.includes('booking')) return 'blue';
  if (value.includes('parking') || value.includes('access')) return 'orange';
  if (value.includes('weather')) return 'sky';
  if (value.includes('freshness')) return 'rose';
  if (value.includes('social')) return 'cyan';
  if (value.includes('address') || value.includes('phone') || value.includes('email') || value.includes('website') || value.includes('source') || value.includes('contact')) return 'teal';
  if (value.includes('new listing') || value.includes('create card')) return 'violet';
  if (value.includes('missing') || value.includes('needs fix') || value.includes('warning')) return 'amber';
  return stableToneForText(value);
}

export function typeTone(kind = 'proposal') {
  if (kind === 'warning') return 'amber';
  if (kind === 'field_update') return 'blue';
  if (kind === 'card_attribute') return 'teal';
  if (kind === 'card_create') return 'violet';
  if (kind === 'signal') return 'purple';
  if (kind === 'new_listing') return 'violet';
  return 'stone';
}

export function sourceTone(source = 'system') {
  const value = String(source || 'system').toLowerCase().replace(/[\s-]+/g, '_');
  if (value === 'system' || value === 'systems') return 'indigo';
  if (value === 'enrichment' || value === 'card_enrichment_worker' || value === 'enrichment_agent') return 'violet';
  if (value === 'scrape_agent' || value.includes('scrape')) return 'sky';
  if (value === 'business_intake' || value.includes('business')) return 'green';
  if (value === 'guest_place_suggestion' || value.includes('guest_suggestion') || value.includes('place_suggestion')) return 'orange';
  if (value === 'guest_report' || value.includes('report')) return 'rose';
  return 'stone';
}

export function Badge({ kind = 'neutral', children }) {
  return (
    <span className={pillClass(tierTone(kind))}>
      {children}
    </span>
  );
}

export function ProposalLabelPill({ label, count, tier = 'suggestion', active = false, onClick, title }) {
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={title}
      data-tier={tier}
      className={pillClass(proposalLabelTone(label), active, 'label')}
    >
      {label}{count > 1 ? ` · ${count}` : ''}
    </Tag>
  );
}

export function filterChipClass(kind = 'neutral', active = false) {
  if (kind === 'blocking' || kind === 'correction' || kind === 'suggestion') {
    return pillToneClass(tierTone(kind), active);
  }
  return pillToneClass(kind, active);
}

export function FilterChip({ active, children, onClick, kind = 'neutral' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${filterChipClass(kind, active)}`}
    >
      {children}
    </button>
  );
}

export function isEditableShortcutTarget(target) {
  const tag = target?.tagName?.toLowerCase?.();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || Boolean(target?.isContentEditable);
}

export function proposalLabelMetaFromItems(items, groupedListMode) {
  const meta = new Map();
  const absorb = (label, tier = 'suggestion', count = 1) => {
    if (!label) return;
    const current = meta.get(label);
    if (!current) {
      meta.set(label, { tier, count });
      return;
    }
    current.count += count;
    if (PROPOSAL_TIER_ORDER[tier] < PROPOSAL_TIER_ORDER[current.tier]) {
      current.tier = tier;
    }
  };
  if (groupedListMode) {
    items.forEach(group => {
      (group.proposal_labels || []).forEach(label => absorb(label.label, label.tier, label.count || 1));
    });
  } else {
    items.forEach(item => absorb(item.proposal_label || proposalTitle(item), proposalTier(item), 1));
  }
  return meta;
}
